/**
 * 资产服务
 * 处理证书和算力相关的API请求，支持mock数据
 */

import api from './api';
import endpoints from './endpoints';
import { getCurrentUser } from './authService';

// PDF数据缓存 - 避免重复请求
const pdfDataCache = new Map();
const CACHE_EXPIRY = 5 * 60 * 1000; // 5分钟缓存

const assetService = {
  /**
   * 获取证书列表
   * @param {Object} params - 查询参数，包括pagination, filter, sort, userId(可选)
   * @returns {Promise} - 证书列表数据
   */
  getCertificates: async (params = {}) => {
    try {
      // 真实API调用 - 使用统一内容API
      
      // 获取用户ID（优先使用params中的userId，否则使用当前登录用户ID）
      let userId = params.userId;
      if (!userId) {
        const currentUser = getCurrentUser();
        userId = currentUser?.id;
      }
      
      // 构建请求参数 - 使用高级filter.exprs格式
      const requestParams = {
        type: 'certificate',
        pagination: params.pagination || { page: 1, per_page: 6 }
      };
      
      // 添加排序 - 默认按颁发日期降序
      requestParams.sort = {
        field: 'valid_from',
        desc: true
      };
      
      // 初始化filter.exprs数组
      requestParams.filter = { exprs: [] };
      
      // 如果有userId，添加到filter.exprs（使用owner_id过滤）
      if (userId) {
        requestParams.filter.exprs.push({
          field: 'owner_id',
          op: 'EQ',
          values: [userId]
        });
      }
      
      // 如果有其他筛选条件，添加到filter.exprs
      if (params.status) {
        requestParams.filter.exprs.push({
          field: 'status',
          op: 'EQ',
          values: [params.status]
        });
      }
      
      if (params.no) {
        requestParams.filter.exprs.push({
          field: 'no',
          op: 'EQ',
          values: [params.no]
        });
      }
      
      // params.owner_id会覆盖默认的userId（使用owner_id）
      if (params.owner_id) {
        requestParams.filter.exprs = requestParams.filter.exprs.filter(expr => expr.field !== 'owner_id');
        requestParams.filter.exprs.push({
          field: 'owner_id',
          op: 'EQ',
          values: [params.owner_id]
        });
      }

      const response = await api.post(endpoints.assets.search, requestParams);
      
      // 转换API响应格式
      if (response.contents) {
        const transformedResponse = {
          certificates: response.contents.map(content => ({
            id: content.id,
            title: content.fields?.title || '未命名证书',
            description: content.fields?.description || '',
            // certificate的file字段存储的是PDF文件ID，需要构建下载URL
            image: content.fields?.file ? endpoints.attachments.download(content.fields.file) : 'https://picsum.photos/200/150',
            fileId: content.fields?.file || null, // 保存file ID用于下载
            awardType: content.fields?.award_type || null,
            date: content.fields?.valid_from ? new Date(content.fields.valid_from).toLocaleDateString('zh-CN') : '未知日期',
            category: 'Certificate',
            status: content.fields?.status || 'unknown',
            issuer: 'SynNovator',
            no: content.fields?.no || ''
          })),
          pagination: response.metadata || {
            page: params.pagination?.page || 1,
            per_page: params.pagination?.per_page || 6,
            total: response.contents.length
          }
        };
        return transformedResponse;
      }
      
      return response;
    } catch (error) {
      const page = params.pagination?.page || 1;
      const perPage = params.pagination?.per_page || 6;
      return {
        certificates: [],
        pagination: {
          page,
          per_page: perPage,
          total: 0,
          total_pages: 0,
        },
      };
    }
  },

  /**
   * 获取算力资源列表
   * @param {Object} params - 查询参数，包括pagination, filter, sort, userId(可选)
   * @returns {Promise} - 算力资源列表数据
   */
  getComputingResources: async (params = {}) => {
    try {
      // 真实API调用 - 使用ai_agent类型
      
      // 获取用户ID（优先使用params中的userId，否则使用当前登录用户ID）
      let userId = params.userId;
      if (!userId) {
        const currentUser = getCurrentUser();
        userId = currentUser?.id;
      }
      
      // 构建请求参数 - 使用高级filter.exprs格式
      const requestParams = {
        type: 'ai_agent',
        pagination: params.pagination || { page: 1, per_page: 10 }
      };
      
      // 添加排序 - 默认按生效日期降序
      requestParams.sort = {
        field: 'valid_from',
        desc: true
      };
      
      // 初始化filter.exprs数组
      requestParams.filter = { exprs: [] };
      
      // 如果有userId，添加到filter.exprs（使用owner_id过滤）
      if (userId) {
        requestParams.filter.exprs.push({
          field: 'owner_id',
          op: 'EQ',
          values: [userId]
        });
      }
      
      // 如果有其他筛选条件，添加到filter.exprs
      if (params.source) {
        requestParams.filter.exprs.push({
          field: 'source',
          op: 'EQ',
          values: [params.source]
        });
      }
      
      if (params.status) {
        requestParams.filter.exprs.push({
          field: 'status',
          op: 'EQ',
          values: [params.status]
        });
      }
      
      // params.owner_id会覆盖默认的userId（使用owner_id）
      if (params.owner_id) {
        requestParams.filter.exprs = requestParams.filter.exprs.filter(expr => expr.field !== 'owner_id');
        requestParams.filter.exprs.push({
          field: 'owner_id',
          op: 'EQ',
          values: [params.owner_id]
        });
      }

      const response = await api.post(endpoints.assets.search, requestParams);
      
      // 转换API响应格式 - 将ai_agent映射为算力资源
      if (response.contents) {
        const transformedResponse = {
          resources: response.contents.map(agent => ({
            id: agent.id,
            name: agent.fields?.title || '未命名AI Agent',
            description: agent.fields?.description || '',
            tag: agent.fields?.source === 'competition_reward' ? '竞赛奖励' : 
                 agent.fields?.source === 'personal_import' ? '个人接入' : 
                 agent.fields?.source || '未知来源',
            used: 0, // AI Agent没有使用算力的概念，设为0
            total: 100, // 模拟总算力值
            status: agent.fields?.status || 'unknown',
            type: agent.fields?.source || '',
            no: agent.fields?.no || '',
            inserted_at: agent.fields?.valid_from || agent.created_at,
            expires_at: agent.fields?.valid_until
          })),
          pagination: response.metadata || {
            page: params.pagination?.page || 1,
            per_page: params.pagination?.per_page || 10,
            total: response.contents.length
          }
        };
        return transformedResponse;
      }
      return response;
    } catch (error) {
      const page = params.pagination?.page || 1;
      const perPage = params.pagination?.per_page || 10;
      return {
        resources: [],
        pagination: {
          page,
          per_page: perPage,
          total: 0,
          total_pages: 0,
        },
      };
    }
  },
  
  /**
   * 获取资产统计信息
   * @param {string} userId - 可选，指定用户ID，不传则使用当前登录用户
   * @returns {Promise} - 资产统计数据
   */
  getAssetStats: async (userId) => {
    try {
      // 真实API调用 - 通过两次search请求获取统计

      // 如果没有传入userId，则使用当前登录用户ID
      let targetUserId = userId;
      if (!targetUserId) {
        const currentUser = getCurrentUser();
        targetUserId = currentUser?.id;
      }
      
      try {
        // 获取证书统计
        const certParams = { type: 'certificate', pagination: { page: 1, per_page: 1 } };
        if (targetUserId) {
          certParams.filter = { exprs: [{ field: 'owner_id', op: 'EQ', values: [targetUserId] }] };
        }
        const certResponse = await api.post(endpoints.assets.search, certParams);
        const certificateCount = certResponse.metadata?.total || 0;
        
        // 获取AI Agent统计（算力资源）
        const agentParams = { type: 'ai_agent', pagination: { page: 1, per_page: 1 } };
        if (targetUserId) {
          agentParams.filter = { exprs: [{ field: 'owner_id', op: 'EQ', values: [targetUserId] }] };
        }
        const agentResponse = await api.post(endpoints.assets.search, agentParams);
        const agentCount = agentResponse.metadata?.total || 0;
      
        
        return {
          certificates: certificateCount,
          computing: agentCount
        };
      } catch (statsError) {
        // 返回空统计数据
        return {
          certificates: 0,
          computing: 0
        };
      }
    } catch (error) {
      return {
        certificates: 0,
        computing: 0,
      };
    }
  },

  /**
   * 通过 AI Agent 的内容ID获取真实密钥（仅owner可读）
   * @param {string} agentId
   * @returns {Promise<string|null>}
   */
  getAgentKeyByAgentId: async (agentId) => {
    try {
      if (!agentId || typeof agentId !== 'string') return null;
      const detail = await api.get(endpoints.assets.detail(agentId), { params: { populate: 'agent_key' } });
      const relation = detail?.fields?.agent_key || detail?.fields?.['agent_key'];
      const key = relation?.fields?.key || relation?.fields?.['key'];
      return typeof key === 'string' ? key : null;
    } catch (err) {
      return null;
    }
  },

  /**
   * 下载证书PDF文件
   * @param {string} fileId - PDF文件的ID
   * @returns {Promise<Blob>} - 文件Blob对象
   */
  downloadCertificate: async (fileId) => {
    try {
      if (!fileId) {
        throw new Error('证书文件ID不存在');
      }

      // 调用API下载文件
      const response = await api.get(endpoints.attachments.download(fileId), {
        responseType: 'blob'
      });

      return response;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 获取PDF文件数据（用于PDF.js处理）
   * @param {string} url - PDF文件的URL
   * @returns {Promise<ArrayBuffer>} - PDF文件的ArrayBuffer
   */
  fetchPdfData: async (url) => {
    try {
      if (!url) {
        throw new Error('PDF URL不存在');
      }

      // 处理不同类型的 URL
      if (url.startsWith('blob:') || url.startsWith('data:')) {
        // 本地文件处理
        if (url.startsWith('blob:')) {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return await response.arrayBuffer();
        } else {
          // data URL处理
          const base64 = url.split(',')[1];
          return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
        }
      }

      // 远程文件处理 - 使用缓存避免重复请求
      const now = Date.now();
      const cachedData = pdfDataCache.get(url);
      
      if (cachedData && (now - cachedData.timestamp) < CACHE_EXPIRY) {
        return cachedData.data;
      }

      // 使用api.get下载文件，指定responseType为blob
      const blob = await api.get(url, { responseType: 'blob' });

      // 将Blob转换为ArrayBuffer
      const arrayBuffer = await blob.arrayBuffer();

      // 缓存结果
      pdfDataCache.set(url, {
        data: arrayBuffer,
        timestamp: now
      });
      
      return arrayBuffer;
    } catch (error) {
      throw error;
    }
  }
};

export default assetService;
